// Core imports
import { useMemo, useState, useEffect, Fragment, useRef } from 'react';
import { createSearchParams, Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import Highlighter from 'react-highlight-words';
import moment from 'moment-timezone';

// Component imports
import { Table, Space, Tooltip, Typography, Row, Col, Button, Input, Popconfirm, Spin, Tag, Progress, Form, Grid, Modal } from 'antd';
import BotAvatar from '../UI/BotAvatar';
import Prompt from '../UI/Prompt';
import SideModal from '../Layout/SideModal';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams, useObservatoryTenantsFilterState } from '../UI/ObservatoryFiltersRow';
import AutomatedAgentTestsCloneForm from '../Forms/AutomatedAgentTestsCloneForm';
import { ThunderboltIcon } from '../Icons/ThunderboltIcon';
import { SearchOutlined, HighlightOutlined, DeleteOutlined, LoadingOutlined, CloseOutlined, CopyOutlined, EyeOutlined } from '@ant-design/icons';
import { EditIcon } from '../Icons/EditIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { COLOR_STATUS_TYPES, DEFAULT_DATETIME_FORMAT, DEFAULT_DATETIME_TIMEZONE_FORMAT, JOB_TYPES, DATETIME_BACKEND_FORMAT, AUTOMATED_TESTS_PERCENT_FILTER, RESULT_THRESHOLDS } from '../../constants/main';
import { useCreateAutomatedTestsMutation, useDeleteAutomatedTestMutation, useFetchAutomatedTestByIdQuery, useFetchAutomatedTestsQuery, useUpdateAutomatedTestMutation } from '../../store/automatedTests/automatedTestsApi';
import { ListCheckIcon } from '../Icons/ListCheckIcon';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { PLUGIN_CATEGORY } from '../../constants/integrations';
import { useJobPolling } from '../../utils/hooks/useJobPolling';
import { NAV_ORGANIZATION_RESULTS } from '../../constants/navigation';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import AutomatedAgentTestsForm from '../Forms/AutomatedAgentTestsForm';

// Constant declarations
const { useBreakpoint } = Grid;
const PAGE_SIZE = 20;
const ADD_NEW_KEYWORD = 'new';

const AutomatedAgentTestsTable = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [filtersForm] = Form.useForm();
  const formRef = useRef(null);

  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || PAGE_SIZE, 10);
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const testIdParam = searchParams.get('testId');
  const isCreateMode = testIdParam === ADD_NEW_KEYWORD;

  const { data: organization, isLoading: isLoadingOrganization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [isHoveringPollingIconId, setIsHoveringPollingIconId] = useState(null);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [selectedAgentTestToClone, setSelectedAgentTestToClone] = useState({});

  const isAgentTestingPluginActive = (tenantId) => {
    const bot = bots?.items?.find(bot => bot.id === tenantId);
    return bot?.plugins?.some(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING) ?? false;
  };

  const { showModalConfirm } = useModalConfirm(formIsDirty);
  const [deleteAutomatedTest, { isLoading: isDeletingTest }] = useDeleteAutomatedTestMutation();

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);

  const observatoryFiltersKey = useMemo(()=> {
    return JSON.stringify(observatoryFilters || []);
  }, [observatoryFilters]);

  const prevObservatoryFiltersKeyRef = useRef(observatoryFiltersKey);

  const initialTenantFilters = useInitialTenantFilters();
  const { shouldSkipTenantsFilter } = useObservatoryTenantsFilterState(observatorySearchParams);

  const { isLoading: isCreatingTest } = useCreateAutomatedTestsMutation()[1];
  const { isLoading: isUpdatingTest } = useUpdateAutomatedTestMutation()[1];

  const { data: automatedTests, isFetching: isFetchingTests, refetch: refetchAutomatedTests } = useFetchAutomatedTestsQuery({
    orgId,
    urlParams: {
      page: currentPage > 0 ? currentPage - 1 : currentPage,
      pageSize,
      sortBy: searchParams.get('sortBy') || 'createdAt',
      order: searchParams.get('order') || 'desc',
      tenantsFilter: observatorySearchParams.tenantsFilter,
      ...(searchParams.get('search') && { searchText: searchParams.get('search') }),
      ...(searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.GTE) && { percentGte: searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.GTE) }),
      ...(searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.LTE) && { percentLte: searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.LTE) })
    }
  }, {
    skip: !orgId || shouldSkipTenantsFilter,
    selectFromResult: (result) => ({
      ...result,
      data: {
        ...result.data,
        itemsMap: new Map(result.data?.items?.map(item => [item.id, item]) || [])
      }
    })
  });

  const { data: singleTest, isFetching: isFetchingSingleTest } = useFetchAutomatedTestByIdQuery(
    { orgId, id: testIdParam },
    { skip: !orgId || !testIdParam || isCreateMode }
  );

  const {
    state: jobPollingState,
    actions: jobPollingActions
  } = useJobPolling({
    orgId,
    type: JOB_TYPES.AUTO_BOT_TEST,
    onJobFinished: () => {
      refetchAutomatedTests();
    }
  });

  useEffect(() => {
    if (prevObservatoryFiltersKeyRef.current !== observatoryFiltersKey) {
      prevObservatoryFiltersKeyRef.current = observatoryFiltersKey;
      handleTableChange({ shouldResetPagination: true });
    }
  }, [observatoryFiltersKey, observatoryFilters.tenantsFilter, observatoryFilters.startDate, observatoryFilters.endDate]);

  const handleShowConfirm = (defaultAction) => {
    showModalConfirm({
      defaultAction: () => {
        setFormIsDirty(false);
        defaultAction();
      },
      onConfirm: async () => {
        const success = await formRef.current?.submit();
        if (success) {
          setFormIsDirty(false);
          defaultAction();
        }
      },
      okText: t('Save Changes'),
      cancelText: t('Quit without saving'),
      title: t('You have unsaved changes'),
      content: t('Do you want to save them before proceeding?')
    });
  };

  const handleDeleteTest = async (testId) => {
    if (searchParams.get('testId') === testId) {
      handleCloseSideModal();
    }
    await deleteAutomatedTest({ orgId, id: testId }).unwrap();
  };

  const debouncedUpdateUrl = useDebouncedCallback((value) => {
    const urlSearchParams = new URLSearchParams(searchParams);
    if (value) {
      urlSearchParams.set('search', value);
    } else {
      urlSearchParams.delete('search');
    }
    urlSearchParams.set('page', '1');
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  }, 300);

  const activeResultFilter = useMemo(() => {
    const percentGte = searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.GTE);
    const percentLte = searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.LTE);

    if (percentGte === `${AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD}` && !percentLte) {
      return RESULT_THRESHOLDS.PASSED;
    }
    if (percentGte === `${AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD}` && percentLte === `${AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD - 1}`) {
      return RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT;
    }
    if (!percentGte && percentLte === `${AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD - 1}`) {
      return RESULT_THRESHOLDS.FAILED;
    }

    return null;
  }, [searchParams]);

  const activeAutomatedTest = useMemo(() => {
    if (!testIdParam) {
      return null;
    }
    if (isCreateMode) {
      return { isCreateMode: true };
    }

    return singleTest || automatedTests?.items?.find(test => test.id === testIdParam) || null;
  }, [testIdParam, isCreateMode, singleTest, automatedTests?.items]);

  const handleOpenModal = (record) => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set('testId', record.id);
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const handleAddNewAutomatedTest = () => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set('testId', ADD_NEW_KEYWORD);
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedUpdateUrl(value);
  };

  const handleCloseSideModal = () => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.delete('testId');
    urlSearchParams.delete('tab');
    setTimeout(() => {
      navigate({ search: urlSearchParams.toString() }, { replace: true });
    }, 100);
  };

  const openCloneModal = (record) => {
    setIsCloneModalOpen(true);
    setSelectedAgentTestToClone(record);
  };
  const closeCloneModal = () => {
    setIsCloneModalOpen(false);
    setSelectedAgentTestToClone({});
  };

  const handleTableChange = (
    {
      pagination = {
        current: Number(searchParams.get('page')) + 1,
        pageSize: Number(searchParams.get('pageSize')) || pageSize
      },
      filters = {
        examinedTenantId: searchParams.get('agents')?.split(',') || null,
        lastResultSuccessRate: searchParams.get(AUTOMATED_TESTS_PERCENT_FILTER.GTE) || null
      },
      sorter = {
        field: searchParams.get('sortBy') || 'createdAt',
        order: searchParams.get('order') || 'desc'
      },
      shouldResetPagination = false
    } = {}
  ) => {
    const urlSearchParams = new URLSearchParams(searchParams);

    urlSearchParams.set('page', shouldResetPagination ? 1 : pagination.current);
    urlSearchParams.set('pageSize', pagination.pageSize);

    if (sorter.field && sorter.order) {
      urlSearchParams.set('sortBy', sorter.field);
      urlSearchParams.set('order', sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      urlSearchParams.set('sortBy', 'createdAt');
      urlSearchParams.set('order', 'desc');
    }

    if (filters.examinedTenantId) {
      urlSearchParams.set('agents', filters.examinedTenantId.join(','));
    } else {
      urlSearchParams.delete('agents');
    }

    urlSearchParams.delete(AUTOMATED_TESTS_PERCENT_FILTER.GTE);
    urlSearchParams.delete(AUTOMATED_TESTS_PERCENT_FILTER.LTE);

    if (filters.lastResultSuccessRate && filters.lastResultSuccessRate.length > 0) {
      const filterValue = filters.lastResultSuccessRate[0];
      switch (filterValue) {
        case RESULT_THRESHOLDS.PASSED:
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.GTE, AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD);
          break;
        case RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT:
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.GTE, AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD);
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.LTE, AUTOMATED_TESTS_PERCENT_FILTER.UPPER_THRESHOLD - 1);
          break;
        case RESULT_THRESHOLDS.FAILED:
          urlSearchParams.set(AUTOMATED_TESTS_PERCENT_FILTER.LTE, AUTOMATED_TESTS_PERCENT_FILTER.LOWER_THRESHOLD - 1);
          break;
        default:
          break;
      }
    }

    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const sortingInfo = useMemo(() => {
    const order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    const columnKey = searchParams.get('sortBy') || 'createdAt';
    return { order, columnKey };
  }, [searchParams.get('sortBy'), searchParams.get('order')]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: jobPollingState.pollingJobsByObjectIdMap.has(record.id),
      name: record.name
    }),
    selections: [
      {
        key: 'all',
        text: t('All'),
        onSelect: () => {
          const selectableKeys = automatedTests?.items
            ?.filter(item => !jobPollingState.pollingJobsByObjectIdMap.has(item.id))
            .map(item => item.id);
          setSelectedRowKeys(selectableKeys || []);
        }
      },
      {
        key: 'none',
        text: t('None'),
        onSelect: () => {
          setSelectedRowKeys([]);
        }
      }
    ]
  };

  const columns = [
    {
      title: t('Test Name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'name' && sortingInfo.order,
      render: (text, record) => {
        const currentSearch = (searchParams.get('search') || '').toLowerCase();

        return (
          <Space>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[searchTerm]}
              autoEscape
              textToHighlight={text ? text.toString() : ''}
            />
            {
              currentSearch && !record.name?.toLowerCase().includes(currentSearch) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('Match found in description')}>
                  <HighlightOutlined className="text-primary" />
                </Tooltip>
              ) : null
            }
          </Space>
        );
      }
    },
    {
      title: t('Agent', { context: 'AI agent' }),
      dataIndex: 'examinedTenantId',
      key: 'examinedTenantId',
      render: (tenantId) => {
        const bot = bots?.items?.find(b => b.id === tenantId);
        return bot ? <BotAvatar avatarSize={24} bot={bot} /> : tenantId;
      }
    },
    {
      title: t('Created at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'createdAt' && sortingInfo.order,
      render: (date) => (date ? moment(date).tz(organization?.timezone).format(DEFAULT_DATETIME_FORMAT) : '-')
    },
    {
      title: t('Last Run'),
      dataIndex: 'testRunDate',
      key: 'testRunDate',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'testRunDate' && sortingInfo.order,
      render: (date) => (date ? moment(date).tz(organization?.timezone).format(DEFAULT_DATETIME_FORMAT) : 'N/A')
    },
    {
      title: t('Sessions Passed'),
      dataIndex: 'lastResultSuccessRate',
      key: 'lastResultSuccessRate',
      align: 'center',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'lastResultSuccessRate' && sortingInfo.order,
      filteredValue: activeResultFilter ? [activeResultFilter] : null,
      filterMultiple: false,
      filters: [
        { text: t('Passed (>=80%)'), value: RESULT_THRESHOLDS.PASSED },
        { text: t('Passed (70-79%)'), value: RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT },
        { text: t('Failed (<70%)'), value: RESULT_THRESHOLDS.FAILED }
      ],
      render: (rate, record) => {
        if (!record.testRunDate) {
          return '-';
        }

        let color;
        if (rate >= 80) {
          color = COLOR_STATUS_TYPES.SUCCESS;
        } else if (rate >= 70) {
          color = COLOR_STATUS_TYPES.WARNING;
        } else {
          color = COLOR_STATUS_TYPES.ERROR;
        }

        const formattedRate = rate % 1 === 0 ? rate : rate.toFixed(2);
        return <Tag className="fw-600 py-1 px-3" color={color}>{`${formattedRate}%`}</Tag>;
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 240,
      align: 'center',
      render: (record) => {
        const isRunning = jobPollingState.pollingJobsByObjectIdMap.has(record.id);
        const isPluginActive = isAgentTestingPluginActive(record.examinedTenantId);

        const hasResults = !!record.testRunDate;
        let resultsLinkParams = { ...observatorySearchParams, automatedTestId: record.id };

        if (hasResults) {
          resultsLinkParams.startDate = moment(record.createdAt).tz(orgTimezone).startOf('day').format(DATETIME_BACKEND_FORMAT);
          resultsLinkParams.endDate = moment(record.testRunDate).tz(orgTimezone).endOf('day').format(DATETIME_BACKEND_FORMAT);
        }

        return (
          <Space onClick={(e) => e.stopPropagation()}>
            <Tooltip
              title={
                !isPluginActive ? (
                  t('Testing plugin is deactivated for this agent')
                ) : isRunning ? (
                  <Space direction="vertical" className="w-100 d-flex justify-content-center align-items-center">
                    <Typography.Text strong>{t('Progress:')}</Typography.Text>
                    <Progress percent={jobPollingState?.pollingJobsByObjectIdMap?.get(record.id)?.progress} type="circle" />
                  </Space>
                ) : (
                  <Typography.Text>{t('Run Test')}</Typography.Text>
                )
              }
            >
              <Button
                shape="circle"
                type="text"
                disabled={!isPluginActive}
                onMouseEnter={() => {
                  setIsHoveringPollingIconId(record.id);
                }}
                onMouseLeave={() => {
                  setIsHoveringPollingIconId(null);
                }}
                icon={isRunning
                  ? isHoveringPollingIconId === record.id
                    ? <CloseOutlined />
                    : <LoadingOutlined spin />
                  : <ThunderboltIcon />
                }
                onClick={async () => {
                  if(isRunning && isHoveringPollingIconId === record.id) {
                    if(jobPollingState?.pollingJobsByObjectIdMap?.has(record.id)) {
                      const jobIdToCancel = jobPollingState?.pollingJobsByObjectIdMap?.get(record.id)?.id;
                      await jobPollingActions.cancelJob(jobIdToCancel);
                      setIsHoveringPollingIconId(null);
                    }
                    return;
                  }
                  await handleOnTestRun(record.id);
                }}
              />
            </Tooltip>

            <Tooltip title={hasResults ? t('View Results') : t('No results available')}>
              {hasResults ? (
                <Link
                  to={`${replaceUrlParams(NAV_ORGANIZATION_RESULTS, { orgId })}?${createSearchParams(resultsLinkParams).toString()}`}
                  replace
                >
                  <Button
                    shape="circle"
                    type="text"
                    icon={<ListCheckIcon />}
                  />
                </Link>
              ) : (
                <Button
                  shape="circle"
                  type="text"
                  disabled
                  icon={<ListCheckIcon />}
                />
              )}
            </Tooltip>

            <Tooltip title={isPluginActive ? t('Edit Test') : t('View Test')}>
              <Button
                shape="circle"
                type="text"
                icon={isPluginActive ? <EditIcon /> : <EyeOutlined />}
                onClick={() => handleOpenModal(record)}
              />
            </Tooltip>

            <Tooltip overlayClassName="pointer-events-none" title={t('Clone Test')}>
              <Button
                shape="circle"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => openCloneModal(record)}
              />
            </Tooltip>

            <Popconfirm
              title={t('Delete this test?')}
              description={t('This action cannot be undone.')}
              onConfirm={() => handleDeleteTest(record.id)}
              okText={t('Delete')}
              cancelText={t('Cancel')}
              placement="topRight"
            >
              <Tooltip title={t('Delete Test')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  const handleOnFormSubmit = async () => {
    if (formRef.current) {
      const result = await formRef.current.submit();

      if (result && result.id) {
        setFormIsDirty(false);
        handleCloseSideModal();
        return result.id;
      }
    }
    return null;
  };

  const handleOnTestRun = async (automatedTestIdToRun) => {
    if (!automatedTestIdToRun) {
      return;
    }

    const startDate = encodeURIComponent(moment().tz(orgTimezone).startOf('day').format(DATETIME_BACKEND_FORMAT));
    const endDate = encodeURIComponent(moment().tz(orgTimezone).endOf('day').format(DATETIME_BACKEND_FORMAT));
    const navLink = `${window.location.origin}${replaceUrlParams(NAV_ORGANIZATION_RESULTS, { orgId })}?startDate=${startDate}&endDate=${endDate}`;
    await jobPollingActions.createAndTrackJobs([
      {
        objectId: automatedTestIdToRun,
        type: JOB_TYPES.AUTO_BOT_TEST,
        description: `${automatedTests?.itemsMap?.get(automatedTestIdToRun)?.name}`,
        navLink
      }
    ]);
  };

  const handleBulkRun = async () => {
    const startDate = encodeURIComponent(moment().tz(orgTimezone).startOf('day').format(DATETIME_BACKEND_FORMAT));
    const endDate = encodeURIComponent(moment().tz(orgTimezone).endOf('day').format(DATETIME_BACKEND_FORMAT));
    const navLink = `${window.location.origin}${replaceUrlParams(NAV_ORGANIZATION_RESULTS, { orgId })}?startDate=${startDate}&endDate=${endDate}`;

    const jobsToCreate = selectedRowKeys.map(testId => ({
      objectId: testId,
      type: JOB_TYPES.AUTO_BOT_TEST,
      description: `${automatedTests?.itemsMap?.get(testId)?.name}`,
      navLink
    }));
    await jobPollingActions.createAndTrackJobs(jobsToCreate);
    setSelectedRowKeys([]);
  };

  const handleOnSubmitAndRun = async () => {
    const automatedTestId = await handleOnFormSubmit();
    await handleOnTestRun(automatedTestId);
  };

  const isCurrentTestInSideModalRunning = !!activeAutomatedTest && jobPollingState.pollingJobsByObjectIdMap.has(activeAutomatedTest.id);

  const isLoading = isLoadingOrganization || isFetchingTests || isFetchingBots || isDeletingTest;

  if (isLoadingOrganization || !organization) {
    return (
      <Row className="d-flex justify-content-center align-items-center">
        <Col >
          <Spin />
        </Col>
      </Row>
    );
  }

  return (
    <Fragment>
      <Prompt
        when={formIsDirty}
        message={t('You have unsaved changes. Are you sure you want to leave?')}
      />

      <Form
        layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
        name="surveyFiltersForm"
        initialValues={{
          tenantsFilter: initialTenantFilters,
          rangePicker: [observatoryFilters.startDate, observatoryFilters.endDate]
        }}
        form={filtersForm}
      >
        <div className="w-100">
          <ObservatoryFiltersRow
            form={filtersForm}
            rangePickerProps={{
              disabled: true,
              timezone: orgTimezone
            }}
            agentsPickerProps={{
              showDeployments: true,
              agentsList: bots?.items || [],
              disabled: isFetchingTests || isLoading
            }}
          />
        </div>

        <Row gutter={24} className="mb-2 w-100" justify={'space-between'}>
          <Col flex="auto">
            <Space className="flex-wrap">
              <Input
                size="large"
                placeholder={t('Search in contents')}
                prefix={<SearchOutlined className="text-secondary" />}
                allowClear
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Space>
          </Col>

          <Col>
            <Space>
              <Button
                block
                size="large"
                onClick={handleBulkRun}
                disabled={selectedRowKeys.length === 0}
              >
                {t('Bulk Run')}
              </Button>

              <Button
                icon={<PlusIcon />}
                type="primary"
                className="float-right"
                size="large"
                onClick={handleAddNewAutomatedTest}
                loading={isCreatingTest}
              >
                {t('Add Test')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            dataSource={automatedTests?.items}
            columns={columns}
            rowSelection={rowSelection}
            scroll={{ y: 680 }}
            className="scroll-table"
            rowClassName={(record) => {
              return record.id === activeAutomatedTest?.id ? 'cursor-pointer bg-table-row-selected' : 'cursor-pointer';
            }}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleOpenModal(record)
            })}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => {
              handleTableChange({ pagination, filters, sorter });
            }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: automatedTests?.total || 0,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Tests')}: ${total}`
            }}
          />
        </Col>
      </Row>

      <SideModal
        open={!!testIdParam}
        onClose={() => handleShowConfirm(handleCloseSideModal)}
        title={isCreateMode ? t('Create New Test') : (activeAutomatedTest ? `${t('Edit')} ${activeAutomatedTest?.name}` : t('Loading...'))}
        bodyClassName="p-0"
        destroyOnClose
        width={900}
        hasFullScreenButton
      >
        <div className="px-6 pt-6 flex-grow-1">
          {isFetchingSingleTest ? (
            <Row justify="center" align="middle" className="h-100 min-h-250">
              <Spin />
            </Row>
          ) : (
            <AutomatedAgentTestsForm
              activeAutomatedTest={activeAutomatedTest}
              ref={formRef}
              onFormChange={setFormIsDirty}
            />
          )}
        </div>

        <div className="ant-drawer-footer">
          <Space className="d-flex justify-content-between">
            <Typography.Paragraph
              ellipsis
              className={`${isCreateMode && 'opacity-0'} text-secondary fs-sm mb-0`}
            >
              {
                !isCreateMode && activeAutomatedTest ? (
                  <Fragment>
                    <span className="fw-bold">{t('Created')}:</span> {moment(activeAutomatedTest?.createdAt).tz(organization?.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}<br/>
                    <span className="fw-bold">{t('Last Run')}:</span> {activeAutomatedTest?.testRunDate ? moment(activeAutomatedTest?.testRunDate).tz(organization?.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT) : 'N/A'}<br/>
                  </Fragment>
                ) : null
              }
            </Typography.Paragraph>

            <Row justify="end" gutter={8}>
              <Col>
                <Button
                  size="large"
                  onClick={handleCloseSideModal}
                  disabled={isCreatingTest || isUpdatingTest || isFetchingSingleTest}
                >
                  {t('Cancel')}
                </Button>
              </Col>

              {(isCreateMode || isAgentTestingPluginActive(activeAutomatedTest?.examinedTenantId)) && (
                <Fragment>
                  <Col>
                    <Button
                      loading={isCreatingTest || isUpdatingTest}
                      disabled={isCreatingTest || isUpdatingTest || isFetchingSingleTest}
                      className="float-right"
                      size="large"
                      onClick={handleOnFormSubmit}
                    >
                      {isCreateMode ? t('Create Test') : t('Save Changes')}
                    </Button>
                  </Col>

                  <Col>
                    <Button
                      loading={isCreatingTest || isUpdatingTest || isCurrentTestInSideModalRunning}
                      disabled={isCreatingTest || isUpdatingTest || isCurrentTestInSideModalRunning || isFetchingSingleTest}
                      className="float-right"
                      type="primary"
                      size="large"
                      onClick={handleOnSubmitAndRun}
                    >
                      {isCreateMode ? t('Create & Run') : t('Save & Run')}
                    </Button>
                  </Col>
                </Fragment>
              )}
            </Row>
          </Space>
        </div>
      </SideModal>

      <Modal
        open={isCloneModalOpen}
        onCancel={closeCloneModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        className="ant-modal-p-0"
        centered
        closable={false}
      >
        <Row className="bg-layout-gray border-radius-top-left-base border-radius-top-right-base p-4">
          <Col xs={20} className="d-flex align-items-center">
            <Typography.Title level={3} className="mb-0">
              {t('Clone Test')}
            </Typography.Title>
          </Col>
          <Col xs={4} className="mt-2">
            <CloseOutlined className="float-right fs-xl cursor-pointer" onClick={() => {
              closeCloneModal();
            }}
            />
          </Col>
        </Row>
        <Row gutter={24} justify="center" className="p-4 border-radius-bottom-left-base border-radius-bottom-right-base">
          <Col xs={24}>
            <Typography.Text className="text-muted">
              {t('Cloned Test Details:')}
            </Typography.Text>
          </Col>
          <Col xs={24}>
            <AutomatedAgentTestsCloneForm
              agentTestToClone={selectedAgentTestToClone}
              onSuccess={closeCloneModal}
              onCancel={closeCloneModal}
            />
          </Col>
        </Row>
      </Modal>

    </Fragment>
  );
};

export default AutomatedAgentTestsTable;
